import * as util from "@/util";

export const ERR_CODE = {
  AuthInvalid: "auth/invalid",
  InvalidParameter: "service/invalidParameter",
  NotImplemented: "service/notImplemented",
  ServiceBadConfig: "service/badConfiguration",
  ServiceBadResponseFormat: "service/badResponseFormat",
  ServiceUnhandled: "service/unhandledError",
  ServiceNetwork: "service/network",
  ServiceRemoteError: "service/remoteError",
  ApiUnhandled: "api/unhandledError",
  ApiParseError: "api/parseError",
  ApiRemote: "api/remoteError"
};

export interface MessagesIndex {
  [index: string]: string;
}

const localErrors: MessagesIndex = {
  network_error: "Problema al intentar conectar al servidor, asegúrese de tener conexión a internet.",
  app_unhandled: "Se produjo un error inesperado, pruebe de nuevo.",
  service_unhandled: "Se produjo un error remoto inesperado.",
  service_generic: "Se produjo un error inesperado, pruebe de nuevo.",
  service_invalid_parameter: "Verifique los datos ingresados.",
  "pay-invalid-state": "Esta solicitud no admite la acción enviada.",
  "pay-branch-disabled": "Contacte con PAYTEF para habilitar Payment Link en esta oficina.",
  "pay-branch-not-allowed": "Esta oficina esta deshabilitada, require configuración.",
  "pay-not-found": "Solicitud de pago no encontrada."
};

const firebaseErrors: MessagesIndex = {
  "auth/user-not-found": "No se encontró una cuenta de usuario con el correo especificado",
  "auth/email-already-in-use": "Este correo ya está siendo usado por otro usuario",
  "auth/user-disabled": "Este usuario ha sido deshabilitado",
  "auth/operation-not-allowed": "Operación no permitida",
  "auth/invalid-email": "Correo electrónico no valido, verifique los datos.",
  "auth/wrong-password": "Contraseña incorrecta, verifique los datos.",
  "auth/network-error": "Problema al intentar conectar al servidor, asegúrese de tener conexión a internet",
  "auth/weak-password": "Contraseña muy débil o no válida, asegúrese que sea de al menos 10 caracteres.",
  "auth/missing-email": "Hace falta registrar un correo electrónico",
  "auth/internal-error": "Error interno, reintente la operación.",
  "auth/invalid-custom-token": "Token personalizado inválido",
  "auth/too-many-requests": "Se han enviado muchas solicitudes al servidor, espere un momento antes de reintentar.",
  "auth/invalid-verification-code": "Código de verificación incorrecto, verifique los datos.",
  "auth/invalid-phone-number": "Número de teléfono no válido, verifique los datos.",
  "auth/code-expired": "El código de verificación expiró, solicite uno nuevo.",
  "auth/missing-verification-code": "Ingrese el código de verificación.",
  "auth/second-factor-already-in-use": "Este teléfono ya está registrado como segundo factor.",
  "auth/maximum-second-factor-count-exceeded": "Ya se alcanzó el máximo de factores de verificación permitidos."
};

export class AppError extends Error {
  userMessage?: string;
  code: string;

  constructor(code: string, message: string, userMessage: string | undefined, cause?: Error) {
    super(`${code}: ${message}`, { cause });
    this.userMessage = util.isEmpty(userMessage) ? message : userMessage;
    this.code = code;
    Object.setPrototypeOf(this, AppError.prototype);
  }

  override toString() {
    return `AppError ${this.code}: ${this.message}`;
  }
}

export type AnyErrorType = Partial<AppError> | null | undefined;

export const utils = {
  HasCode(err: AnyErrorType) {
    return !this.GetCode(err);
  },

  GetCode(err: AnyErrorType) {
    if (!err || typeof err === "string" || !err.code) {
      return null;
    }
    return err.code;
  },

  IsInvalidParam(err: AnyErrorType) {
    return this.IsCode(err, [ERR_CODE.InvalidParameter]);
  },

  IsUnhandled(err: AnyErrorType) {
    return this.IsCode(err, [
      ERR_CODE.ServiceUnhandled,
      ERR_CODE.ServiceBadResponseFormat,
      ERR_CODE.ServiceBadConfig,
      ERR_CODE.AuthInvalid,
      ERR_CODE.ServiceUnhandled,
      ERR_CODE.NotImplemented,
      ERR_CODE.ApiUnhandled,
      ERR_CODE.ApiParseError
    ]);
  },

  IsCode(err: AnyErrorType, codes: string[]) {
    const code = this.GetCode(err);
    if (!code) {
      return false;
    }
    return codes.includes(code);
  },

  GetCodeDescription(err: AnyErrorType, defaultText: string): string {
    if (!err) {
      return defaultText;
    }
    if (err.userMessage) {
      return err.userMessage;
    }
    const code = this.GetCode(err);
    if (code) {
      const msg = getErrMessage(code, firebaseErrors) || getErrMessage(code, localErrors);
      if (msg) {
        return msg;
      }
      // App error or firebase error
      // TODO add a big switch to return proper message
      switch (err.code) {
        case ERR_CODE.ServiceNetwork:
          return localErrors.network_error;
        case ERR_CODE.ServiceUnhandled:
          return localErrors.service_unhandled;
        case ERR_CODE.ApiUnhandled:
          return localErrors.app_unhandled;
        case ERR_CODE.ServiceRemoteError:
          return localErrors.service_generic;
        case ERR_CODE.InvalidParameter:
          return localErrors.service_invalid_parameter;
        default:
          return defaultText || localErrors.service_generic;
      }
    }
    return defaultText || localErrors.service_generic;
  },

  Log(err: AnyErrorType, place: string, defaultText: string) {
    util.logError("Error in", place, ":", err, defaultText);
  }
};

function getErrMessage(code: string, catalog: MessagesIndex): string | null {
  if (catalog[code]) {
    return catalog[code] as string;
  }
  return null;
}
